import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Search,
  People,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  Email,
  Phone,
  Work,
  CalendarToday,
  Schedule,
  Badge,
  EventAvailable,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { MobileFAB } from '../../components/Mobile'
import {
  fetchEmployees,
  setFilters,
  deleteEmployee,
  createEmployee,
  updateEmployee,
} from '../../store/slices/employeesSlice'
import { useEmployeeUpdates } from '../../hooks/useWebSocket'
import { usePullToRefresh } from '../../hooks/useSwipeGesture'
import { createStaggerAnimation } from '../../utils/mobileAnimations'
import { EmployeeDialog } from '../../components/Employees/EmployeeDialog'
import { EmployeeDetailsDialog } from '../../components/Employees/EmployeeDetailsDialog'
import { ConfirmDialog } from '../../components/Common/ConfirmDialog'
import { AttendanceManagementPageFinal } from '../attendance/AttendanceManagementPageFinal'
import EmployeeCalendarView from '../../components/Employees/EmployeeCalendarView'
import HolidayList from '../../components/Employees/HolidayList'

const DEPARTMENTS = [
  { value: '', label: 'All' },
  { value: 'production', label: 'Production' },
  { value: 'quality_control', label: 'QC' },
  { value: 'design', label: 'Design' },
  { value: 'management', label: 'Management' },
]

/**
 * Employees Mobile View
 * Card-based employee list with tabs for different views
 */
export const EmployeesMobileView: React.FC = () => {
  const dispatch = useAppDispatch()
  const { employees, total, isLoading, filters, pagination } = useAppSelector((state) => state.employees)
  const { employees: realtimeEmployees } = useEmployeeUpdates()

  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [selectedDepartment, setSelectedDepartment] = useState(filters.department || '')
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuEmployee, setMenuEmployee] = useState<any>(null)
  const [currentTab, setCurrentTab] = useState(0)

  // Pull to refresh
  const { handlers, isPulling, isRefreshing } = usePullToRefresh(async () => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    await dispatch(fetchEmployees(params))
  })

  // Load employees
  useEffect(() => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchEmployees(params))
  }, [dispatch, filters, pagination])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    setTimeout(() => {
      dispatch(setFilters({ search: value }))
    }, 500)
  }

  const handleDepartmentFilter = (department: string) => {
    setSelectedDepartment(department)
    dispatch(setFilters({ department: department || undefined }))
  }

  const handleCreateEmployee = () => {
    setSelectedEmployee(null)
    setEmployeeDialogOpen(true)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, employee: any) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setMenuEmployee(employee)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuEmployee(null)
  }

  const handleViewEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setDetailsDialogOpen(true)
    handleMenuClose()
  }

  const handleEditEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setEmployeeDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setConfirmDialogOpen(true)
    handleMenuClose()
  }

  const confirmDelete = async () => {
    if (selectedEmployee) {
      await dispatch(deleteEmployee(selectedEmployee.id || 0))
      setConfirmDialogOpen(false)
      setSelectedEmployee(null)
    }
  }

  const handleEmployeeSubmit = async (employeeData: any) => {
    try {
      if (selectedEmployee && selectedEmployee.id) {
        await dispatch(updateEmployee({
          employeeId: selectedEmployee.id,
          employeeData
        })).unwrap()
      } else {
        await dispatch(createEmployee(employeeData)).unwrap()
      }
      setEmployeeDialogOpen(false)
      setSelectedEmployee(null)
    } catch (error) {
      console.error('Error submitting employee:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'default'
      case 'on_leave':
        return 'warning'
      case 'terminated':
        return 'error'
      default:
        return 'default'
    }
  }

  const getDepartmentColor = (department: string) => {
    switch (department.toLowerCase()) {
      case 'production':
        return 'primary'
      case 'quality_control':
        return 'secondary'
      case 'design':
        return 'info'
      case 'management':
        return 'warning'
      default:
        return 'default'
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  return (
    <Box sx={{ pb: 10, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Enhanced Tabs */}
      <Box sx={{
        bgcolor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            '& .MuiTabs-flexContainer': {
              gap: 0.5,
            },
            '& .MuiTab-root': {
              minHeight: 64,
              minWidth: 'auto',
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.813rem',
              color: 'text.secondary',
              borderRadius: '12px',
              mx: 0.5,
              my: 1,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: 'rgba(102, 126, 234, 0.08)',
                color: '#667eea',
              },
              '&.Mui-selected': {
                color: 'white',
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)',
              },
              '& .MuiTab-iconWrapper': {
                marginBottom: '4px',
                fontSize: '1.25rem',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scrollButtons': {
              color: '#667eea',
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          }}
        >
          <Tab
            icon={<People sx={{ fontSize: 20 }} />}
            label="List"
            iconPosition="top"
          />
          <Tab
            icon={<CalendarToday sx={{ fontSize: 20 }} />}
            label="Attendance"
            iconPosition="top"
          />
          <Tab
            icon={<Schedule sx={{ fontSize: 20 }} />}
            label="Calendar"
            iconPosition="top"
          />
          <Tab
            icon={<EventAvailable sx={{ fontSize: 20 }} />}
            label="Holidays"
            iconPosition="top"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box
          onTouchStart={handlers.onTouchStart as any}
          onTouchMove={handlers.onTouchMove as any}
          onTouchEnd={handlers.onTouchEnd as any}
        >
          {/* Pull to Refresh Indicator */}
          {isPulling && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                zIndex: 1000,
              }}
            >
              <Typography variant="caption">
                {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
              </Typography>
            </Box>
          )}

          {/* Search and Filters */}
          <Box sx={{ px: 3, pt: 2, mb: 2 }}>
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search employees..."
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: 'none',
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Department Filter Chips */}
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 2 }}>
              {DEPARTMENTS.map((dept) => (
                <Chip
                  key={dept.value}
                  label={dept.label}
                  onClick={() => handleDepartmentFilter(dept.value)}
                  sx={{
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    px: 1,
                    bgcolor: selectedDepartment === dept.value ? '#667eea' : 'white',
                    color: selectedDepartment === dept.value ? 'white' : 'text.primary',
                    border: selectedDepartment === dept.value ? 'none' : '1px solid #e0e0e0',
                    boxShadow: selectedDepartment === dept.value ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
                    '&:hover': {
                      bgcolor: selectedDepartment === dept.value ? '#5568d3' : '#f5f5f5',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Employees List */}
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              Team Members
            </Typography>
            {isLoading && employees.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Loading employees...</Typography>
              </Box>
            ) : employees && employees.length > 0 ? (
              <Stack spacing={2.5}>
                {employees.map((employee: any, index: number) => (
                  <Card
                    key={employee.id || employee.employee_id}
                    onClick={() => handleViewEmployee(employee)}
                    sx={{
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      border: 'none',
                      overflow: 'hidden',
                      bgcolor: 'white',
                      ...createStaggerAnimation(index, 30),
                      '&:active': {
                        transform: 'scale(0.97)',
                      },
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      {/* Header with Avatar */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            mr: 2,
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                          }}
                        >
                          {employee.full_name?.charAt(0) || 'E'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {employee.full_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Badge sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {employee.employee_id}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, employee)}
                          sx={{
                            bgcolor: '#f5f5f5',
                            '&:hover': {
                              bgcolor: '#e0e0e0',
                            },
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>

                      {/* Position Badge */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 2,
                          py: 0.75,
                          bgcolor: '#f0f4ff',
                          borderRadius: '20px',
                          mb: 2,
                        }}
                      >
                        <Work sx={{ fontSize: 14, color: '#667eea' }} />
                        <Typography variant="body2" fontWeight={600} color="#667eea">
                          {employee.position}
                        </Typography>
                      </Box>

                      {/* Contact Details - Horizontal Layout */}
                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        {employee.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                bgcolor: '#e8f5e9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Email sx={{ fontSize: 16, color: '#2e7d32' }} />
                            </Box>
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                              {employee.email}
                            </Typography>
                          </Box>
                        )}
                        {employee.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                bgcolor: '#e3f2fd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Phone sx={{ fontSize: 16, color: '#1976d2' }} />
                            </Box>
                            <Typography variant="body2">
                              {employee.phone}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      {/* Salary and Status Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Monthly Salary
                          </Typography>
                          <Typography variant="h6" fontWeight={700} color="primary.main">
                            Rs. {employee.salary?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip
                            label={employee.status}
                            size="small"
                            sx={{
                              borderRadius: '12px',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              bgcolor:
                                employee.status?.toLowerCase() === 'active'
                                  ? '#e8f5e9'
                                  : employee.status?.toLowerCase() === 'on_leave'
                                  ? '#fff3e0'
                                  : '#ffebee',
                              color:
                                employee.status?.toLowerCase() === 'active'
                                  ? '#2e7d32'
                                  : employee.status?.toLowerCase() === 'on_leave'
                                  ? '#f57c00'
                                  : '#c62828',
                              border: 'none',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Department Tag */}
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: '#fafafa',
                          borderRadius: '12px',
                          borderLeft: '4px solid #667eea',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Department
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {employee.department}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Card sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '20px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}>
                <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  No employees found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || selectedDepartment
                    ? 'Try adjusting your filters'
                    : 'Add your first employee to get started'}
                </Typography>
              </Card>
            )}
          </Box>

          {/* Floating Action Button */}
          <Box
            onClick={handleCreateEmployee}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              bgcolor: '#667eea',
              color: 'white',
              borderRadius: '20px',
              px: 3,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <People />
            <Typography variant="body2" fontWeight={600}>
              Add Employee
            </Typography>
          </Box>
        </Box>
      )}

      {/* Attendance Tab */}
      {currentTab === 1 && (
        <Box
          sx={{
            p: 0,
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            '& > *': {
              maxWidth: '100%',
            }
          }}
        >
          <Box sx={{
            p: 1,
            maxWidth: '100%',
            overflow: 'hidden',
            '& .MuiCard-root': {
              maxWidth: '100%',
              overflow: 'visible',
              margin: '8px 0 !important',
              boxSizing: 'border-box',
            },
            '& .MuiCardContent-root': {
              padding: '8px !important',
              maxWidth: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box',
            },
            '& .MuiGrid-root': {
              maxWidth: '100%',
              margin: '0 !important',
              width: '100% !important',
              boxSizing: 'border-box',
            },
            '& .MuiGrid-container': {
              width: '100% !important',
              margin: '0 !important',
              maxWidth: '100%',
            },
            '& .MuiGrid-item': {
              paddingLeft: '2px !important',
              paddingRight: '2px !important',
              paddingTop: '2px !important',
              paddingBottom: '2px !important',
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
            // Typography adjustments
            '& .MuiTypography-h4': {
              fontSize: '1.25rem !important',
              lineHeight: '1.4 !important',
            },
            '& .MuiTypography-h5': {
              fontSize: '1.1rem !important',
              lineHeight: '1.4 !important',
            },
            '& .MuiTypography-h6': {
              fontSize: '0.95rem !important',
              lineHeight: '1.3 !important',
            },
            '& .MuiTypography-body1': {
              fontSize: '0.8rem !important',
              lineHeight: '1.3 !important',
            },
            '& .MuiTypography-body2': {
              fontSize: '0.7rem !important',
              lineHeight: '1.2 !important',
            },
            // TextField adjustments
            '& .MuiTextField-root': {
              width: '100% !important',
              maxWidth: '100%',
              margin: '4px 0 !important',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem !important',
              padding: '8px !important',
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px !important',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.8rem !important',
            },
            // Chip adjustments - CRITICAL FOR STATUS CHIPS
            '& .MuiChip-root': {
              fontSize: '0.65rem !important',
              height: '24px !important',
              margin: '2px !important',
              maxWidth: '100%',
              '& .MuiChip-icon': {
                fontSize: '0.9rem !important',
                marginLeft: '4px !important',
                marginRight: '-2px !important',
              },
            },
            '& .MuiChip-label': {
              padding: '0 6px !important',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            // Button adjustments
            '& .MuiButton-root': {
              fontSize: '0.75rem !important',
              padding: '4px 8px !important',
              minWidth: 'auto !important',
              whiteSpace: 'nowrap',
            },
            '& .MuiIconButton-root': {
              padding: '6px !important',
            },
            // Select/Autocomplete adjustments
            '& .MuiSelect-root': {
              fontSize: '0.8rem !important',
              padding: '8px !important',
            },
            '& .MuiSelect-select': {
              padding: '8px !important',
            },
            '& .MuiAutocomplete-root': {
              width: '100% !important',
              maxWidth: '100%',
            },
            '& .MuiAutocomplete-input': {
              fontSize: '0.8rem !important',
              padding: '4px !important',
            },
            // DatePicker adjustments
            '& .MuiPickersDay-root': {
              fontSize: '0.7rem !important',
            },
            // DataGrid adjustments
            '& .MuiDataGrid-root': {
              maxWidth: '100%',
              overflow: 'auto',
              fontSize: '0.7rem !important',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.7rem !important',
              padding: '2px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.7rem !important',
              padding: '2px !important',
            },
            // Stack adjustments - CRITICAL FOR CHIP CONTAINERS
            '& .MuiStack-root': {
              maxWidth: '100%',
              flexWrap: 'wrap !important',
              gap: '4px !important',
              overflow: 'visible !important',
            },
            // Box adjustments
            '& .MuiBox-root': {
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
            // FormControl adjustments
            '& .MuiFormControl-root': {
              width: '100% !important',
              maxWidth: '100%',
              margin: '2px 0 !important',
            },
            // Ensure flex containers wrap and don't overflow
            '& > div': {
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
            // Status chips container - FORCE WRAPPING
            '& .MuiStack-root > .MuiChip-root': {
              flexShrink: 1,
              minWidth: 0,
            },
            // Search bar container
            '& form': {
              width: '100% !important',
              maxWidth: '100%',
            },
            // Force all direct children to respect width
            '& > * > *': {
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
            // Specific fix for date picker and chips row
            '& .MuiBox-root:has(.MuiChip-root)': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              maxWidth: '100%',
              overflow: 'visible',
            },
          }}>
            <AttendanceManagementPageFinal />
          </Box>
        </Box>
      )}

      {/* Calendar Tab */}
      {currentTab === 2 && (
        <Box
          sx={{
            p: 0,
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            '& > *': {
              maxWidth: '100%',
            }
          }}
        >
          <Box sx={{
            p: 2,
            '& .MuiCard-root': {
              maxWidth: '100%',
              overflow: 'hidden',
              borderRadius: '8px !important',
            },
            '& .MuiGrid-root': {
              maxWidth: '100%',
              margin: 0,
              width: '100%',
            },
            '& .MuiGrid-item': {
              paddingLeft: '8px !important',
              paddingRight: '8px !important',
            },
            // Make calendar bigger
            '& .MuiPickersCalendarHeader-root': {
              padding: '16px !important',
            },
            '& .MuiDayCalendar-header': {
              justifyContent: 'space-around',
              padding: '8px 0',
            },
            '& .MuiDayCalendar-weekContainer': {
              justifyContent: 'space-around',
              margin: '4px 0',
            },
            '& .MuiPickersDay-root': {
              width: '40px !important',
              height: '40px !important',
              fontSize: '0.9rem !important',
              margin: '2px !important',
              borderRadius: '8px !important',
            },
            '& .MuiPickersDay-today': {
              borderWidth: '2px !important',
            },
            '& .MuiTypography-root': {
              fontSize: '0.9rem !important',
            },
            '& .MuiPickersCalendarHeader-label': {
              fontSize: '1.1rem !important',
              fontWeight: 600,
            },
          }}>
            <EmployeeCalendarView />
          </Box>
        </Box>
      )}

      {/* Holidays Tab */}
      {currentTab === 3 && (
        <Box
          sx={{
            p: 0,
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            '& > *': {
              maxWidth: '100%',
            }
          }}
        >
          <Box sx={{
            p: 2,
            '& .MuiCard-root': {
              maxWidth: '100%',
              overflow: 'hidden',
              borderRadius: '8px !important',
            },
          }}>
            <HolidayList />
          </Box>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={() => menuEmployee && handleViewEmployee(menuEmployee)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuEmployee && handleEditEmployee(menuEmployee)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Employee</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <Schedule fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Attendance</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuEmployee && handleDeleteEmployee(menuEmployee)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <EmployeeDialog
        open={employeeDialogOpen}
        onClose={() => setEmployeeDialogOpen(false)}
        onSubmit={handleEmployeeSubmit}
        employee={selectedEmployee}
      />

      <EmployeeDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        employee={selectedEmployee}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete ${selectedEmployee?.full_name}? This action cannot be undone.`}
      />
    </Box>
  )
}
